import CheckIcon from "@mui/icons-material/Check";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import PhoneIcon from "@mui/icons-material/Phone";
import { Box, Chip, MenuItem, Select, SelectChangeEvent, Typography } from "@mui/material";

import { useEffect, useMemo } from "react";
import { formatUSPhone } from "../../../../utils";
import { useVirtualAssistant } from "../../context/VirtualAssistantContext";
import { CompanyNumber } from "../../widget";
import { useCompanyNumberQuery } from "../../widget/components/NumbersConfiguration/hooks/useCompanyNumberQuery";
import { CompanyNumberStatus } from "../../porting-request/hooks/useCompanyNumbersMultiQuery";

const ORANGE = "#E87722";
const SELECT_Z_INDEX = 99999;

export function MobileLineSelector() {
  const { data: numbersData = [] } = useCompanyNumberQuery();
  const { selectedNumberUid, setSelectedNumberUid } = useVirtualAssistant();

  const activeNumbers = useMemo(
    () => numbersData.filter((number: CompanyNumber) => number.status === CompanyNumberStatus.ACTIVE),
    [numbersData]
  );

  const activeNumber = activeNumbers.find((number: CompanyNumber) => number.uid === selectedNumberUid);

  useEffect(() => {
    if (activeNumbers.length > 0) {
      const isCurrentSelectedValid = activeNumbers.some((number) => number.uid === selectedNumberUid);
      if (!isCurrentSelectedValid) {
        setSelectedNumberUid(activeNumbers[0].uid);
      }
    }
  }, [activeNumbers, selectedNumberUid, setSelectedNumberUid]);

  if (activeNumbers.length === 0) return null;

  return (
    <Box sx={{ mb: 1 }}>
      <Select
        value={selectedNumberUid}
        onChange={(event: SelectChangeEvent<string>) => setSelectedNumberUid(event.target.value)}
        displayEmpty
        fullWidth
        size="small"
        IconComponent={KeyboardArrowDownIcon}
        renderValue={() => {
          if (!activeNumber) return null;

          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: "8px",
                  backgroundColor: `${ORANGE}15`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <PhoneIcon sx={{ fontSize: 14, color: ORANGE }} />
              </Box>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                {activeNumber.name && (
                  <Typography
                    sx={{
                      fontSize: 10,
                      color: "text.disabled",
                      lineHeight: 1.2,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {activeNumber.name}
                  </Typography>
                )}
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: "text.primary",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    lineHeight: 1.3,
                  }}
                >
                  {formatUSPhone(activeNumber.phoneNumber)}
                </Typography>
              </Box>
            </Box>
          );
        }}
        sx={{
          borderRadius: "12px",
          backgroundColor: (theme) => (theme.palette.mode === "dark" ? "rgba(var(--fx-surface-basergb, 255, 255, 255), 0.04)" : "rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.02)"),
          "& .MuiOutlinedInput-notchedOutline": { borderColor: "divider", borderWidth: "1px" },
          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: `${ORANGE}80` },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: ORANGE, borderWidth: "1.5px" },
          "& .MuiSelect-select": { py: "10px !important", px: "12px !important" },
          "& .MuiSelect-icon": { color: "text.disabled", fontSize: 20, right: 10 },
        }}
        MenuProps={{
          disablePortal: true,
          keepMounted: false,
          sx: { zIndex: SELECT_Z_INDEX },
          PaperProps: {
            elevation: 0,
            sx: {
              zIndex: SELECT_Z_INDEX,
              borderRadius: "14px",
              mt: 0.75,
              border: "1px solid",
              borderColor: "divider",
              boxShadow: "0 8px 32px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)",
              overflow: "hidden",
              "& .MuiList-root": { py: 1, px: 0.75 },
            },
          },
        }}
      >
        {activeNumbers.map((number: CompanyNumber) => {
          const isSelected = number.uid === selectedNumberUid;

          return (
            <MenuItem
              key={number.uid}
              value={number.uid}
              disableRipple
              sx={{
                px: 1.25,
                py: 1.25,
                borderRadius: "10px",
                gap: 1.25,
                transition: "background 0.15s",
                "&.Mui-selected": {
                  backgroundColor: `${ORANGE}10`,
                  "&:hover": { backgroundColor: `${ORANGE}18` },
                },
                "&:hover": { backgroundColor: "action.hover" },
              }}
            >
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: "9px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: isSelected
                    ? `${ORANGE}15`
                    : (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)"),
                  flexShrink: 0,
                }}
              >
                <PhoneIcon sx={{ fontSize: 15, color: isSelected ? ORANGE : "text.secondary" }} />
              </Box>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                {number.name && (
                  <Typography
                    sx={{
                      fontSize: 10,
                      color: "text.disabled",
                      lineHeight: 1.2,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {number.name}
                  </Typography>
                )}
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: isSelected ? 600 : 500,
                    color: isSelected ? ORANGE : "text.primary",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    lineHeight: 1.3,
                  }}
                >
                  {formatUSPhone(number.phoneNumber)}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexShrink: 0 }}>
                <Chip
                  label={number.source}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: 10,
                    fontWeight: 600,
                    backgroundColor: isSelected
                      ? `${ORANGE}15`
                      : (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)"),
                    color: isSelected ? ORANGE : "text.secondary",
                    borderRadius: "6px",
                    "& .MuiChip-label": { px: 1 },
                  }}
                />
                {isSelected && <CheckIcon sx={{ fontSize: 15, color: ORANGE }} />}
              </Box>
            </MenuItem>
          );
        })}
      </Select>
    </Box>
  );
}
